import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Progress from 'redux-progress';
import { RESTRICTED_COUNTRIES_ISO_CODES, RESTRICTED_EMAIL_DOMAINS } from 'modules/utils';
import { extractTLDFrom } from 'modules/siteconfigurator/utils';
import {
  getCurrentMACContactDetails,
  getCurrentMACContactEmail,
  selectIsSwitchedUser,
} from 'modules/mac/duck/selectors';
import {
  BTFProductTypes,
  isAcademicOrNoneLicenseType,
  isAddOnType,
  isAdvisoryServicesProduct,
  isCommercialOrAcademicOrDeveloperOrStarterOrOpenSourceLicenseType,
  isCommercialOrAcademicOrDeveloperOrStarterOrOpenSourceOrSocialImpactLicenseType,
  isCommercialOrAcademicOrDeveloperType,
  isSocialImpactLicenseType,
  isSocialImpactOrOpenSourceLicenseType,
  notPartOfForRenewKeys,
  pricingPlanBambooDC,
  sourceTreeKeys,
  isSupportOnlyProductKey,
} from 'util/mac/helpers';
import { FreeExtensionAppliedMessage } from './FreeExtensionAppliedMessage';
import { StyledButtonsWrapper, StyledFlex } from '../StyledComponents';
import { ActionsMessage } from './ActionsMessage';
import { RenewLink } from './RenewLink';
import { UpgradeLink } from './UpgradeLink';
import { DownloadLink } from './DownloadLink';
import { DownloadSourceLink } from './DownloadSourceLink';
import { ViewDeveloperLicense } from './ViewDeveloperLicense';
import { ArchiveUnarchiveButtonLink } from './ArchiveUnarchiveButtonLink';
import ViewDeveloperLicenseModalDialog from './ViewDeveloperLicenseModal';

type NotHostedAccountProps = {
  isSiteOnConcession: boolean;
  isRenewableAccount: Progress<boolean>;
  isUpgradableAccount: Progress<boolean>;
  showSourceDownloadBtn: boolean;
  isArchiveButtonVisible: boolean;
  isArchived: boolean;
  isAccount: boolean;
  accountDetails: any;
  productPackage: any;
};

export const NotHostedAccount = ({
  isSiteOnConcession,
  isRenewableAccount,
  isUpgradableAccount,
  isArchiveButtonVisible,
  isArchived,
  isAccount,
  accountDetails,
  productPackage,
  showSourceDownloadBtn,
}: NotHostedAccountProps) => {
  const {
    id: accountId,
    active: isAccountActive,
    accountPriceOverrideType,
    pricingPlan,
    latestPeriod: { endDate, productFeatureUsage },
    merchandise: {
      details: { licenseType },
    },
  } = accountDetails;
  const {
    key,
    productType,
    hostingType,
    billingType,
    pricingPlanProduct,
    isDataCenter,
    downloadProduct,
    description,
  } = productPackage;

  const [isRUBYRestricted, setIsRUBYRestricted] = useState(true);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const isSwitchedUser = useSelector(selectIsSwitchedUser) || false;

  const currentMACContactEmail = useSelector(getCurrentMACContactEmail);
  const currentMACContactDetails = useSelector(getCurrentMACContactDetails);

  useEffect(() => {
    if (currentMACContactDetails && currentMACContactEmail) {
      const isISORestricted = RESTRICTED_COUNTRIES_ISO_CODES.includes(
        (currentMACContactDetails as any)?.organisationDetails?.isoCountryCode,
      );
      const isEmailRestricted = RESTRICTED_EMAIL_DOMAINS.includes(
        extractTLDFrom(currentMACContactEmail),
      );
      if (!isISORestricted && !isEmailRestricted) {
        setIsRUBYRestricted(false);
      }
    }
  }, [currentMACContactDetails, currentMACContactEmail]);

  if (
    (billingType === 'PERPETUAL' || pricingPlanProduct || isAddOnType(productType)) &&
    !sourceTreeKeys.includes(key)
  ) {
    return (
      <>
        {isSiteOnConcession && <FreeExtensionAppliedMessage />}
        {hostingType !== 'TRAINING' &&
          !isAdvisoryServicesProduct(key) &&
          isCommercialOrAcademicOrDeveloperOrStarterOrOpenSourceOrSocialImpactLicenseType(
            licenseType,
          ) &&
          !isRUBYRestricted && (
            <StyledFlex>
              <ActionsMessage />
              <StyledButtonsWrapper>
                {!notPartOfForRenewKeys.includes(key) &&
                  !isSwitchedUser &&
                  (isSupportOnlyProductKey(key) || isRenewableAccount) && (
                    <RenewLink accountId={accountId} />
                  )}
                {BTFProductTypes.includes(productType) &&
                  !isSwitchedUser &&
                  isDataCenter &&
                  isAccountActive &&
                  !isSocialImpactOrOpenSourceLicenseType(accountPriceOverrideType) &&
                  key !== pricingPlanBambooDC &&
                  isUpgradableAccount && (
                    <UpgradeLink accountId={accountId} productPackageKey={key} />
                  )}
                {(isAddOnType(licenseType) || downloadProduct?.downloadUrl) && (
                  <DownloadLink
                    downloadUrl={downloadProduct?.downloadUrl}
                    productKey={key}
                    description={description}
                  />
                )}
                {!isSocialImpactLicenseType(accountPriceOverrideType) &&
                  isCommercialOrAcademicOrDeveloperOrStarterOrOpenSourceLicenseType(licenseType) &&
                  downloadProduct?.key &&
                  showSourceDownloadBtn && <DownloadSourceLink productKey={downloadProduct?.key} />}
                {licenseType !== null &&
                  pricingPlan !== null &&
                  productFeatureUsage.starter === false &&
                  (accountPriceOverrideType === null ||
                    isAcademicOrNoneLicenseType(accountPriceOverrideType)) &&
                  !isSupportOnlyProductKey(key) &&
                  !isAdvisoryServicesProduct(key) &&
                  isCommercialOrAcademicOrDeveloperType(licenseType) && (
                    <ViewDeveloperLicense setIsModalOpened={setIsModalOpened} />
                  )}
                <ArchiveUnarchiveButtonLink
                  isArchiveButtonVisible={isArchiveButtonVisible}
                  isArchived={isArchived}
                  accountId={accountId}
                  isAccount={isAccount}
                />
              </StyledButtonsWrapper>
            </StyledFlex>
          )}
        <ViewDeveloperLicenseModalDialog
          id={accountId}
          isModalOpened={isModalOpened}
          setIsModalOpened={setIsModalOpened}
          endDate={endDate}
          isActive={isAccountActive}
          productType={productType}
        />
      </>
    );
  }

  return null;
};
